import React, { useMemo } from 'react';
import qs from 'query-string';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';

import UpgradeModal, { UpgradeProps } from '~/App/Components/Modals/Upgrade';
import useRollUpState from '~/App/Modules/TodoList/use-roll-up-state';
import { TodoCreateMode } from '~/App/Modules/TodoList/Modals/TodoCreate';
import { Greet } from '~/types/models/index';
import {
  openSendWithYourDomainEmailModal,
  SendWithYourDomainEmailModal,
} from '../Modals/SendWithYourDomainEmailModal';
import {
  trackUpgradeModalView,
  UpgradeModalViewContext,
} from '~/App/Services/BillingAnalytics';
import useCurrentUser from '~/react-hooks/use-current-user';
import { showSignupToUpgradeModal } from '~/App/Components/Modals/UpgradeModals/helpers';
import { useFeatures } from '~/react-hooks/use-features';
import { startUpgradeModalFlow } from '~/App/Components/Modals/UpgradeModalFlow';
import { UpgradeModalTrigger } from '~/App/Components/Modals/UpgradeModalFlow/util';
import { usePlandalfGate } from '~/plandalf/use-plandalf-gate';
import { hasRollupAccess } from '~/plandalf/gates';

interface RollUpButtonProps {
  greets: Array<Greet>;
  disabled?: boolean;
}

const getTotalRecipients = (greets: Array<Greet>) => {
  return greets.reduce(
    (total: number, greet: Greet) => (greet.profiles?.length ?? 0) + total,
    0
  );
};

export default function RollUpButton({ greets, disabled }: RollUpButtonProps) {
  const { setRollUp } = useRollUpState();
  const { features, from_signature } = useCurrentUser();
  const { canSwitchPlan } = useFeatures();
  const { trigger: triggerPlandalfGate, checking: checkingPlandalf } =
    usePlandalfGate();

  const totalGreets = greets.length;
  const rollupBtnEnabled: boolean = totalGreets > 1;
  const canRollUp: boolean = useMemo(
    () => features.canRollupGreets && totalGreets > 1,
    [totalGreets, features.canRollupGreets]
  );

  const rollUpLimit = features.greetRollUpLimit;

  const upgradeModalProps: UpgradeProps = {
    referer: 'todo-rollups',
    redirect: '/home',
    onOpen() {
      trackUpgradeModalView(UpgradeModalViewContext.RollupUpsell);
    },
  };

  const shouldShowSendWithDomainEmailModal = useMemo(() => {
    const isSendingUsingDefault = from_signature?.is_default ?? true;
    if (isSendingUsingDefault && !features.canSendRollupsUsingDefaultEmail) {
      return true;
    }

    const isSendingUsingOAuth = from_signature?.is_integration ?? false;
    if (isSendingUsingOAuth && !features.canSendRollupsUsingOAuth) {
      return true;
    }

    return false;
  }, [from_signature, features]);

  // Roll-up limits are defined in terms of the number of greets, not recipients.
  const isOverRollUpLimit =
    rollUpLimit != null && totalGreets > rollUpLimit;
  const shouldShowUpgradeModal =
    !canRollUp || !rollupBtnEnabled || isOverRollUpLimit;

  async function handleRollUpClick(evt: React.MouseEvent) {
    if (!canSwitchPlan) {
      showSignupToUpgradeModal();
      return;
    }

    if (shouldShowUpgradeModal) {
      if (features.canPurchaseRollUpLimitAddOn) {
        trackUpgradeModalView(UpgradeModalViewContext.NewRollupUpsell);
        const result = await triggerPlandalfGate('rollup', () =>
          hasRollupAccess(totalGreets)
        );
        if (result.state === 'allowed') {
          setRollUp(greets);
          window.location.href = `#/todo/create?${qs.stringify({
            mode: TodoCreateMode.ROLLUP,
          })}`;
          return;
        }
        if (result.state !== 'manual' && result.state !== 'legacy') {
          evt.preventDefault();
          return;
        }
      }
      startUpgradeModalFlow({
        trigger: UpgradeModalTrigger.RollupLimit,
        withAddOn: features.canPurchaseRollUpLimitAddOn,
        reason: 'Rollup limit reached',
      });
      evt.preventDefault();
      return;
    }

    if (shouldShowSendWithDomainEmailModal) {
      openSendWithYourDomainEmailModal();
      evt.preventDefault();
      return;
    }

    setRollUp(greets);

    if (canRollUp) {
      window.location.href = `#/todo/create?${qs.stringify({
        mode: TodoCreateMode.ROLLUP,
      })}`;
    }
  }

  return (
    <React.Fragment>
      <Button
        variant={ButtonVariant.Primary}
        onClick={handleRollUpClick}
        className='h-10 py-0 text-base font-bold'
        disabled={!rollupBtnEnabled || disabled || checkingPlandalf}
      >
        <FontAwesomeIcon icon={faRotateRight} />
        <span className='ml-3.5'>Roll-up</span>
      </Button>
      <SendWithYourDomainEmailModal />
      <UpgradeModal {...upgradeModalProps} />
    </React.Fragment>
  );
}
